import React, { useCallback,useEffect,useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COOKIE_IDS } from 'App/constants';
import { ButtonSizes, ButtonWidth } from 'Shared/components/design/button/Button';
import {
  SSO_BUTTON_TEST_IDS,
  SSOButtonOptionsByProvider,
  SSOButtonProps,
  SSOProvider,
} from 'Shared/components/design/SSOButton/constants';
import { SSOButtonBtn } from 'Shared/components/design/SSOButton/style';
import { setCookie } from 'Shared/services/cookieService';
import { handleFacebookLogin, initializeGoogleSSO, loadFacebookSDK, loadGoogleSDK, openSSOProviderWindow } from 'Shared/utils/SSOUtils';

/**
 * SSO Button component
 */
const SSOButton: React.FC<SSOButtonProps> = ({
  provider,
  onClick,
  errorCallback,
  isLoadingCallback,
  isIdpSSO,
  ...props
}) => {
  const googleCodeClientRef = useRef<Window["google"]["accounts"]["oauth2"]["CodeClient"] | null>(null);
  const { t }               = useTranslation('signIn');

  // Setting default values
  const {
    width = ButtonWidth.FULL,
    size  = ButtonSizes.LARGE,
  } = props;

  // Getting provider options
  const {
    name: providerName,
    logoUri,
    ...options
  } = SSOButtonOptionsByProvider[provider];

  /**
   * Triggers error callback with the provider name
   */
  const handleError = useCallback((): void => {
    errorCallback?.(provider);
  }, [errorCallback, provider]);

  /**
   * On mount, depending of provider load and init google/facebook client
   */
  useEffect(() => {
    if (provider === SSOProvider.GOOGLE && isIdpSSO) {
      if (!window.google?.accounts?.oauth2) {
        loadGoogleSDK().then(() => {
          googleCodeClientRef.current = initializeGoogleSSO(handleError, isLoadingCallback);
        }).catch(() => {
          handleError();
        });
      } else {
        googleCodeClientRef.current = initializeGoogleSSO(handleError, isLoadingCallback);
      }
    }

    if (provider === SSOProvider.FACEBOOK && isIdpSSO) {
      if (!window.FB) {
        loadFacebookSDK()
        .catch(() => {
          handleError();
        });
      }
    }
  }, [provider, errorCallback, handleError, isLoadingCallback, isIdpSSO]);

  /**
   * Click handler
   */
  const handleClick = (): void => {
    onClick?.();
    // Setting forceV3SignUp cookie with an expiration date of 1 day
    setCookie(COOKIE_IDS.FORCE_TO_V3_FOR_NEW_SIGNUP, '1', 1, true);

    if (!isIdpSSO) {
       // Opening legacy login popup
      openSSOProviderWindow(provider);
      return;
    }

    // trigger google login
    if (provider === SSOProvider.GOOGLE) {
      if (!googleCodeClientRef.current) {
        handleError();
        return;
      }
      googleCodeClientRef.current.requestCode();
    }

    // trigger facebook login
    if (provider === SSOProvider.FACEBOOK) {
      handleFacebookLogin(handleError, isLoadingCallback);
    }
  };

  return (
    <SSOButtonBtn
      size={size}
      width={width}
      options={options}
      onClick={handleClick}
      data-testid={SSO_BUTTON_TEST_IDS.CONTAINER}
      {...props}
    >
      <img src={logoUri} alt={providerName} />
      {t('SignInForm--SSOButton--text', { providerName })}
    </SSOButtonBtn>
  );
};

export default SSOButton;